import React, { useEffect, useRef } from "react";
import { BrowserRouter, Routes, Route, Link } from "react-router-dom";
import "./App.css";

const MAILTO = "mailto:harvestbowlapp@gmail.com";
const APP_STORE_URL = "https://apps.apple.com/app/harvest-bowl/id6754568085";
const VIDEO_ID = "YkepOVnrVFU";

function Topbar() {
  return (
    <div className="topbar">
      <Link to="/" className="brand" aria-label="Harvest Bowl home">
        <span className="dot" aria-hidden="true" />
        <span className="wordmark">Harvest Bowl</span>
      </Link>
      <div className="top-links">
        <a href={APP_STORE_URL} target="_blank" rel="noreferrer" className="text-link">
          App Store
        </a>
        <Link to="/press" className="text-link">
          Press kit
        </Link>
        <a href={MAILTO} className="text-link">
          Email press
        </a>
        <Link to="/privacy" className="text-link">
          Privacy
        </Link>
      </div>
    </div>
  );
}

function YouTubeEmbed() {
  const playerRef = useRef(null);
  const playerInstanceRef = useRef(null);

  useEffect(() => {
    let isMounted = true;

    const onPlayerReady = (event) => {
      event.target.setPlaybackQuality("hd1080");
    };

    const createPlayer = () => {
      if (!isMounted || !playerRef.current || playerInstanceRef.current) {
        return;
      }

      playerInstanceRef.current = new window.YT.Player(playerRef.current, {
        videoId: VIDEO_ID,
        playerVars: {
          rel: 0,
          modestbranding: 1,
          playsinline: 1,
        },
        events: {
          onReady: onPlayerReady,
        },
      });
    };

    if (window.YT && window.YT.Player) {
      createPlayer();
    } else {
      const previousCallback = window.onYouTubeIframeAPIReady;
      window.onYouTubeIframeAPIReady = () => {
        if (typeof previousCallback === "function") {
          previousCallback();
        }
        createPlayer();
      };

      if (!document.querySelector('script[src="https://www.youtube.com/iframe_api"]')) {
        const tag = document.createElement("script");
        tag.src = "https://www.youtube.com/iframe_api";
        tag.async = true;
        document.head.appendChild(tag);
      }
    }

    return () => {
      isMounted = false;
      if (playerInstanceRef.current) {
        playerInstanceRef.current.destroy();
        playerInstanceRef.current = null;
      }
    };
  }, []);

  return (
    <div className="video-frame">
      <div
        ref={playerRef}
        className="yt-player"
        role="presentation"
        aria-label="Harvest Bowl product walkthrough"
      />
    </div>
  );
}

function Home() {
  return (
    <>
      <div className="page-shell">
        <Topbar />
        <section className="home-hero">
          <div className="home-copy">
            <p className="eyebrow">Fuel on purpose</p>
            <h1>Build precise bowls. Train, eat, and feel consistent.</h1>
            <p className="lede">
              Harvest Bowl lets you set exact grams or calories for every ingredient. Save repeatable meals, hit your
              macros, and move on with your day.
            </p>
            <div className="cta-row">
              <a className="btn primary" href={APP_STORE_URL} target="_blank" rel="noreferrer">
                Download for iPhone
              </a>
              <Link className="btn ghost" to="/press">
                View press kit
              </Link>
            </div>
            <div className="hero-facts">
              <div>
                <p className="muted">Built by</p>
                <p className="fact">Dinesh Sonachalam, distance runner and engineering leader in London.</p>
              </div>
              <div>
                <p className="muted">Promise</p>
                <p className="fact">No ads, no trackers—everything stays on your device.</p>
              </div>
            </div>
          </div>
          <div className="home-panel">
            <div className="panel-card">
              <p className="eyebrow">Core loop</p>
              <h3>Go from empty bowl to saved plan in a minute.</h3>
              <ul className="mini-list">
                <li>Choose protein, carbs, veggies with exact targets.</li>
                <li>Draft bowls for tomorrow’s workout or travel day.</li>
                <li>Adjust on the fly—Harvest Bowl recalculates instantly.</li>
              </ul>
            </div>
            <YouTubeEmbed />
          </div>
        </section>

        <section className="home-grid">
          <article className="press-card">
            <p className="eyebrow">Why it exists</p>
            <h3>Precision beats guessing.</h3>
            <p className="muted">Set exactly what you want to eat and let the app do the math. Perfect for runners, lifters, and disciplined eaters.</p>
          </article>
          <article className="press-card">
            <p className="eyebrow">Offline by design</p>
            <h3>Everything stays on your phone.</h3>
            <p className="muted">No account required. No analytics SDKs. Your bowls stay local unless you choose to share.</p>
          </article>
          <article className="press-card">
            <p className="eyebrow">Need assets?</p>
            <h3>Press kit ready.</h3>
            <p className="muted">Screenshots, logo, and founder photo available anytime.</p>
            <Link className="text-link" to="/press">
              Jump to press kit →
            </Link>
          </article>
        </section>
      </div>
      <Footer />
    </>
  );
}

function PressKit() {
  return (
    <>
      <div className="page-shell">
        <Topbar />

        <section className="press-hero compact">
          <div className="hero-copy">
            <p className="eyebrow">Press kit · iOS</p>
            <h1>Harvest Bowl, in one download.</h1>
            <p className="lede">
              Grab the full asset pack—App Store shots, raw UI, logo, and founder photo. Need a quote or data pull? Email
              and we’ll reply fast.
            </p>
            <div className="cta-row">
              <a className="btn primary" href="/press-kit.zip" download>
                Download press kit (.zip)
              </a>
              <a className="btn ghost" href={MAILTO}>
                Email press
              </a>
              <a className="btn ghost" href={APP_STORE_URL} target="_blank" rel="noreferrer">
                View on App Store
              </a>
            </div>
            <div className="hero-facts">
              <div>
                <p className="muted">Contents</p>
                <p className="fact">10 curated shots · 10 raw UI frames · App Icon 1024px · Founder photo</p>
              </div>
              <div>
                <p className="muted">Need more?</p>
                <p className="fact">Email for quotes, usage stats, or a fresh build.</p>
              </div>
            </div>
          </div>
          <div className="panel-card">
            <p className="eyebrow">Walkthrough</p>
            <h3>60s video</h3>
            <p className="muted">Watch the core flow on YouTube.</p>
            <a className="text-link" href={`https://youtu.be/${VIDEO_ID}`} target="_blank" rel="noreferrer">
              Open video →
            </a>
          </div>
        </section>

        <section className="founder">
          <div className="founder-copy">
            <p className="eyebrow">About Dinesh</p>
            <h2>A running man who ships reliable software.</h2>
            <p className="lede">
              Dinesh Sonachalam spends his time shipping solid software and logging long runs around London’s lakes. He
              prefers simplicity over noise, thoughtful engineering over shortcuts, and conversations that are honest and
              concise.
            </p>
            <div className="socials">
              <a className="text-link" href="https://github.com/dineshsonachalam" target="_blank" rel="noreferrer">
                GitHub
              </a>
              <a className="text-link" href="https://medium.com/@dineshsonachalam" target="_blank" rel="noreferrer">
                Medium
              </a>
              <a className="text-link" href={MAILTO}>
                Email
              </a>
            </div>
          </div>
          <div className="founder-photo">
            <img src="/developer_photo/Dinesh.png" alt="Dinesh Sonachalam running beside the water" loading="lazy" />
          </div>
        </section>
      </div>
      <Footer />
    </>
  );
}

function Privacy() {
  return (
    <>
      <div className="privacy-page">
        <div className="privacy-header">
          <h1>Privacy Policy</h1>
          <p className="effective-date">Effective date: 2025-01-01</p>
          <p className="lede">
            Harvest Bowl respects your privacy. The app is designed to work entirely on your device, and we keep things
            simple—no accounts, no tracking, no data collection.
          </p>
        </div>

        <section className="privacy-grid">
          <article className="privacy-card emphasis">
            <h2>Summary</h2>
            <p>
              <strong>Harvest Bowl does not collect personal data.</strong> Everything you create stays on your iPhone
              unless you choose to share it.
            </p>
          </article>

          <article className="privacy-card">
            <h3>Data We Do Not Collect</h3>
            <ul>
              <li>No names, email addresses, or contact information</li>
              <li>No health or nutrition data</li>
              <li>No location data</li>
              <li>No analytics, ads, or tracking cookies</li>
            </ul>
          </article>

          <article className="privacy-card">
            <h3>Optional Sharing</h3>
            <p>
              If you contact support or share a bowl, the information you send is used only to respond to your request.
              Once resolved, we delete the message.
            </p>
          </article>

          <article className="privacy-card">
            <h3>Third-Party Services</h3>
            <p>Harvest Bowl does not integrate with third-party analytics SDKs, advertising networks, or social login.</p>
          </article>

          <article className="privacy-card">
            <h3>Children&apos;s Privacy</h3>
            <p>Harvest Bowl is suitable for all ages and does not knowingly collect personal information from children.</p>
          </article>

          <article className="privacy-card">
            <h3>Contact</h3>
            <p>
              Questions? Email us at <a href={MAILTO}>harvestbowlapp@gmail.com</a>.
            </p>
          </article>

          <article className="privacy-card">
            <h3>Changes</h3>
            <p>If this policy changes, we will update this page and revise the effective date above.</p>
          </article>
        </section>
      </div>
      <Footer />
    </>
  );
}

function Footer() {
  return (
    <footer className="footer">
      <div className="footer-links">
        <a href={APP_STORE_URL} className="text-link" target="_blank" rel="noopener noreferrer">
          View in the App Store
        </a>
        <span aria-hidden="true">·</span>
        <a href={MAILTO} className="text-link">
          Contact support
        </a>
        <span aria-hidden="true">·</span>
        <Link to="/privacy" className="text-link">
          Privacy Policy
        </Link>
      </div>
      <p>© {new Date().getFullYear()} Harvest Bowl. Built with care for iPhone.</p>
    </footer>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/press" element={<PressKit />} />
        <Route path="/privacy" element={<Privacy />} />
      </Routes>
    </BrowserRouter>
  );
}
